import { useMemo, useState } from 'react';

import { ChevronDown, ChevronUp } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import { Collapse } from 'react-collapse';

import type { useCart } from 'src/api/queries';
import { Button, Divider, Skeleton } from 'src/components';
import { cartHelpers } from 'src/utils/cartHelpers';
import { CA_PROVINCES, US_STATES } from 'src/utils/countryList';

import { useCheckoutForm } from '../checkoutFormStore';

export function CartDetails({ cart }: { cart: ReturnType<typeof useCart> }) {
  const [checkoutFormState] = useCheckoutForm();
  const [showDetails, setShowDetails] = useState(false);

  const priceWithTax = cartHelpers.getPriceWithTaxFormatted(cart.details.data);
  const priceWithoutTax = cartHelpers.getPriceWithoutTaxFormatted(
    cart.details.data
  );
  const tax = cartHelpers.getTaxFormatted(cart.details.data);
  const includesTax = cartHelpers.isTaxIncluded(cart.details.data);

  const isUs = cart.country.data?.value === 'US';
  const usState = useMemo(() => {
    if (isUs) {
      return US_STATES.find((s) => s.value === checkoutFormState.state)?.label;
    }
  }, [checkoutFormState.state]);

  const isCanada = checkoutFormState.country === 'CA';
  const canadaProvince = useMemo(() => {
    if (isCanada) {
      return CA_PROVINCES.find((s) => s.value === checkoutFormState.province)
        ?.label;
    }
  }, [checkoutFormState.province]);

  const taxLocation = usState
    ? `${usState}, US`
    : canadaProvince
      ? `${canadaProvince}, CA`
      : (cart.country.data?.label ?? '');

  const updatingPrice =
    cart.updateCurrency.isPending ||
    cart.updateTax.isPending ||
    cart.setup.isPending;

  const details = (
    <div className="rounded-sm flex flex-col gap-8 p-16">
      <div className="flex justify-between gap-16">
        <Typography variant="body-lg" className="font-medium">
          Connect subscription
        </Typography>
        <Divider className="my-0" />
        <Skeleton isLoading={updatingPrice} className="w-20">
          <Typography variant="body-lg" className="font-medium">
            {includesTax ? priceWithTax : priceWithoutTax}
          </Typography>
        </Skeleton>
      </div>
      <Divider className="my-0" />

      <div className="flex flex-row items-center justify-between">
        <p className="m-0 text-16">
          {isUs ? 'Sales tax' : 'VAT'} to {taxLocation}
        </p>
        <Skeleton isLoading={updatingPrice} className="w-20">
          <p className="m-0 text-16">{includesTax ? 'included' : tax}</p>
        </Skeleton>
      </div>
      {updatingPrice ? null : includesTax ? null : (
        <>
          <Divider className="my-0" />
          <div className="flex flex-row items-center justify-between">
            <Typography variant="body-lg" className="font-semibold">
              Total
            </Typography>
            <Typography variant="body-lg" className="font-semibold">
              {priceWithTax}
            </Typography>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div>
      <div className="flex w-full flex-col bg-neutral-light-3 p-8 ll:hidden">
        <Button
          variant="tertiary-neutral"
          className="w-full bg-none"
          onClick={() => setShowDetails((showDetails) => !showDetails)}
          data-cy="cookie-banner-show-details"
        >
          <span className="flex w-full items-center justify-between">
            <Typography variant="body-md" className="inline">
              Order summary:
            </Typography>
            &nbsp;
            {showDetails ? null : (
              <Skeleton isLoading={updatingPrice} className="w-20">
                <Typography variant="body-md" className="ml-auto inline">
                  {priceWithTax}
                </Typography>
              </Skeleton>
            )}
            {showDetails ? (
              <ChevronUp className="ml-auto inline max-w-20" />
            ) : (
              <ChevronDown className="ml-auto inline max-w-20" />
            )}
          </span>
        </Button>
        <Collapse isOpened={showDetails}>{details}</Collapse>
      </div>

      <div
        data-cy="connect-plan-row-price"
        className="hidden flex-col gap-16 bg-neutral-light-3 ll:flex"
      >
        {details}
      </div>
    </div>
  );
}
